/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import { gsap } from 'gsap';
import { Color, ConeGeometry, Mesh, Object3D, RepeatWrapping, ShaderMaterial, Vector2 } from 'three';

import Loaders from '../../../../utils/Loaders';
import PerformanceProfile from '../../../../utils/PerformanceProfile';

import { fragmentShader, vertexShader } from './shaders';

// Created outisde of class to reuse the same ones
const geometry = new ConeGeometry(1.1, 1.6, 4, 1);

const BLEND_MODES = [
    { text: 'Add' },
    { text: 'Average' },
    { text: 'ColorBurn' },
    { text: 'ColorDodge' },
    { text: 'Darken' },
    { text: 'Difference' },
    { text: 'Exclusion' },
    { text: 'Glow' },
    { text: 'HardLight' },
    { text: 'HardMix' },
    { text: 'Lighten' },
    { text: 'LinearBurn' },
    { text: 'LinearDodge' },
    { text: 'LinearLight' },
    { text: 'Multiply' },
    { text: 'Negation' },
    { text: 'Normal' },
    { text: 'Overlay' },
    { text: 'Phoenix' },
    { text: 'PinLight' },
    { text: 'Reflect' },
    { text: 'Screen' },
    { text: 'SoftLight' },
    { text: 'VividLight' },
];

BLEND_MODES.forEach((blendMode, index) => {
    blendMode.value = index + 1;
});

const GLOBAL_CONFIG = {
    rotationX: 0.1,
    rotationSpeed: 1.5,
    normalMap: null,
    normalMapUrl: '/assets/webgl/textures/floor-game-stained-glass.jpg',
    // normalMapUrl: '/assets/webgl/textures/floor-game-stone.jpg'
};

const CONFIG = [
    // Tier 1:
    {
        color: '#bdbdbd',
        specular: '#f4f4f4',
        refractionFactor: 1.2,
        specularIntensity: 3.0,
        blending: 1.0,
        blendMode: 15,
        reverseBlend: false,
        normalIntensity: 0.7,
        normalScale: 1.0,
    },
    {
        // Tier 2:
        color: '#607fce',
        specular: '#265eed',
        refractionFactor: 1.2,
        specularIntensity: 3.0,
        blending: 1.0,
        blendMode: 15,
        reverseBlend: false,
        normalIntensity: 0.7,
        normalScale: 1.0,
    },
    // Tier 3:
    {
        color: '#935dd6',
        specular: '#8932f2',
        refractionFactor: 1.2,
        specularIntensity: 3.0,
        blending: 1.0,
        blendMode: 15,
        reverseBlend: false,
        normalIntensity: 0.7,
        normalScale: 1.0,
    },
    // Tier 4:
    {
        color: '#ffe071',
        specular: '#ffc701',
        refractionFactor: 1.2,
        specularIntensity: 3.0,
        blending: 1.0,
        blendMode: 15,
        reverseBlend: false,
        normalIntensity: 0.7,
        normalScale: 1.0,
    },
    // Tier 5:
    {
        color: '#ffe071',
        specular: '#ffc701',
        refractionFactor: 1.2,
        specularIntensity: 3.0,
        blending: 1.0,
        blendMode: 15,
        reverseBlend: false,
        normalIntensity: 0.7,
        normalScale: 1.0,
    },
];

let GUI;

const addGUI = async () => {
    await PerformanceProfile.devModeReady();

    GUI = PerformanceProfile.GUI.floorGameUp.addFolder({
        title: `ETH Logo`,
        expanded: false,
    });

    GUI.addButton({ title: 'Export Config' }).on('click', () => {
        PerformanceProfile.exportConfig('ETHLogo', { ...GLOBAL_CONFIG, ...CONFIG });
    });

    GUI.addInput(GLOBAL_CONFIG, 'rotationX', { min: -0.5, max: 0.5, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'rotationSpeed', { min: -5.0, max: 5.0, step: 0.001 });

    for (let i = 0; i < CONFIG.length; i++) {
        const folder = GUI.addFolder({
            title: `Tier ${i + 1}`,
            expanded: false,
        });

        folder.addInput(CONFIG[i], 'color');
        folder.addInput(CONFIG[i], 'specular');
        folder.addInput(CONFIG[i], 'specularIntensity', { min: 0, max: 10, step: 0.001 });
        folder.addInput(CONFIG[i], 'refractionFactor', { min: 1, max: 1.5, step: 0.001 });
        folder.addInput(CONFIG[i], 'normalIntensity', { min: 0, max: 2, step: 0.001 });
        folder.addInput(CONFIG[i], 'normalScale', { min: 0, max: 2, step: 0.0001 });

        folder
            .addBlade({
                view: 'list',
                label: 'blendModes',
                options: BLEND_MODES,
                value: BLEND_MODES[CONFIG[i].blendMode - 1].value,
            })
            .on('change', (blade) => {
                CONFIG[i].blendMode = blade.value;
            });

        folder.addInput(CONFIG[i], 'blending', { min: 0, max: 2, step: 0.001 });
        folder.addInput(CONFIG[i], 'reverseBlend');
    }
};

if (PerformanceProfile.devMode) addGUI();

interface CardCryptoLogoOptions {
    tier?: number;
    imageURL?: string;
    isCollectionView?: boolean;
    // TODO: Rename this property (avoid `use` prefix).
    useNormal?: boolean;
}

export default class CardCryptoLogo extends Object3D {
    animatedRotation: {
        tween: number;
        current: number;
    };

    tier: number;
    color: string;
    specular: string;
    textureURL: string | null;

    isCollectionView: boolean;
    useNormal: boolean;

    // TODO: These need to be explicitly assigned within the constructor.
    material: ShaderMaterial;
    topMesh: Mesh;
    bottomMesh: Mesh;

    visible = false;

    // TODO: `CardFGUp` is the only place `CardCryptoLogo` is used,
    // and it passes a `type` property that does not exist in this constructor.
    constructor({ tier, imageURL, isCollectionView = false, useNormal = true }: CardCryptoLogoOptions) {
        super();

        this.animatedRotation = {
            tween: 0.0,
            current: 0.0,
        };

        this.scale.set(0.3, 0.3, 0.3);
        this.rotation.x = Math.PI * GLOBAL_CONFIG.rotationX;
        this.rotation.y = Math.random() * Math.PI;

        this.tier = tier ?? 0;
        this.color = CONFIG[this.tier].color;
        this.specular = CONFIG[this.tier].specular;

        this.textureURL = imageURL || null;
        this.isCollectionView = isCollectionView;
        this.useNormal = useNormal;

        this.initMaterial();
        this.initMesh();
        this.loadTextures();

        if (PerformanceProfile.devMode) {
            GUI.on('change', this.updateUniforms.bind(this));
        }
    }

    initMaterial() {
        this.material = new ShaderMaterial({
            fragmentShader,
            vertexShader,
            uniforms: {
                uTexture: { value: null },
                uTime: { value: 0.0 },
                uResolution: { value: new Vector2(1.0, 1.0) },
                uAspectRatio: { value: new Vector2(1.0, 1.0) },
                uOffsetX: { value: 0.0 },
                uTransition: { value: 0.0 },

                uColor: { value: new Color(CONFIG[this.tier].color) },
                uSpecular: { value: new Color(CONFIG[this.tier].specular) },
                uSpecularIntensity: { value: CONFIG[this.tier].specularIntensity },

                uNormalMap: { value: null },
                uNormalMapIntensity: { value: CONFIG[this.tier].normalIntensity },
                uNormalMapScale: { value: CONFIG[this.tier].normalScale },

                uRefractionFactor: { value: CONFIG[this.tier].refractionFactor },
                uBlending: { value: CONFIG[this.tier].blending },
                uBlendMode: { value: 15 },
                uReverseBlend: { value: CONFIG[this.tier].reverseBlend ? 1.0 : 0.0 },
            },
            defines: {
                USE_TEXTURE: false,
                USE_NORMAL: this.useNormal,
            },
        });
    }

    initMesh() {
        this.topMesh = new Mesh(geometry, this.material);
        this.bottomMesh = new Mesh(geometry, this.material);

        this.bottomMesh.rotation.x = Math.PI;

        this.topMesh.position.y = 0.9;
        this.bottomMesh.position.y = -0.9;

        this.add(this.topMesh);
        this.add(this.bottomMesh);
    }

    loadTextures() {
        if (this.textureURL) {
            Loaders.textureLoader.load(this.textureURL, (texture) => {
                this.material.uniforms.uTexture.value = texture;
                this.material.uniforms.uTexture.value.needsUpdate = true;

                this.material.defines['USE_TEXTURE'] = true;
                this.material.needsUpdate = true;

                // AspectRatio
                const textureWidth = texture.image.width || 0;
                const textureHeight = texture.image.height || 0;

                let aspectRatioX = 1.0;
                let aspectRatioY = 1.0;

                if (textureWidth > 0) {
                    aspectRatioX = textureWidth < textureHeight ? 1.0 : textureHeight / textureWidth;
                    aspectRatioY = textureWidth < textureHeight ? textureWidth / textureHeight : 1.0;
                }

                this.material.uniforms.uAspectRatio.value.x = aspectRatioX;
                this.material.uniforms.uAspectRatio.value.y = aspectRatioY;
            });
        }

        if (this.useNormal) {
            if (GLOBAL_CONFIG.normalMap) {
                this.material.uniforms.uNormalMap.value = GLOBAL_CONFIG.normalMap;
                this.material.uniforms.uNormalMap.value.needsUpdate = true;
            } else {
                Loaders.textureLoader.load(GLOBAL_CONFIG.normalMapUrl, (texture) => {
                    texture.wrapS = RepeatWrapping;
                    texture.wrapT = RepeatWrapping;

                    GLOBAL_CONFIG.normalMap = texture;

                    this.material.uniforms.uNormalMap.value = texture;
                    this.material.uniforms.uNormalMap.value.needsUpdate = true;
                });
            }
        }
    }

    setResolution(width: number, height: number) {
        this.material.uniforms.uResolution.value.x = width;
        this.material.uniforms.uResolution.value.y = height;

        if (this.isCollectionView) {
            this.position.x = -0.75;
            this.material.uniforms.uOffsetX.value = -1.1;
        } else {
            if (width * 0.5 < 1100) {
                this.position.x = 0;
                this.material.uniforms.uOffsetX.value = 0;
            } else {
                this.position.x = -0.3;
                this.material.uniforms.uOffsetX.value = -0.45;
            }
        }
    }

    update(elapsed: number) {
        this.animatedRotation.current = elapsed * GLOBAL_CONFIG.rotationSpeed;
        this.rotation.y = this.animatedRotation.current + this.animatedRotation.tween;
    }

    animateIn() {
        // this.visible = true;
        this.visible = false;

        gsap.fromTo(
            this.scale,
            {
                x: 0.01,
                y: 0.01,
                z: 0.01,
            },
            {
                x: 0.3,
                y: 0.3,
                z: 0.3,
                duration: 2.5,
                delay: 2.5,
                ease: 'expo.inOut',
                onStart: () => {
                    this.visible = true;
                },
            },
        );

        if (this.isCollectionView) {
            const posX = this.position.x;
            const offsetX = this.material.uniforms.uOffsetX.value;

            gsap.fromTo(
                this.position,
                {
                    x: 0.0,
                },
                {
                    x: posX,
                    duration: 2.0,
                    delay: 2.5,
                    ease: 'expo.inOut',
                },
            );

            gsap.fromTo(
                this.material.uniforms.uOffsetX,
                {
                    value: 0.0,
                },
                {
                    value: offsetX,
                    duration: 2.0,
                    delay: 2.5,
                    ease: 'expo.inOut',
                },
            );
        }

        gsap.fromTo(
            this.animatedRotation,
            {
                tween: 0.0,
            },
            {
                tween: Math.PI * 3.0,
                duration: 3.0,
                delay: 3.4,
                ease: 'expo.out',
            },
        );

        gsap.fromTo(
            this.material.uniforms.uTransition,
            {
                value: 0.0,
            },
            {
                value: 1,
                duration: 1.6,
                delay: 3.8,
                ease: 'power2.out',
            },
        );
    }

    updateUniforms() {
        this.material.uniforms.uColor.value.setStyle(CONFIG[this.tier].color);
        this.material.uniforms.uSpecular.value.setStyle(CONFIG[this.tier].specular);
        this.material.uniforms.uSpecularIntensity.value = CONFIG[this.tier].specularIntensity;
        this.material.uniforms.uRefractionFactor.value = CONFIG[this.tier].refractionFactor;
        this.material.uniforms.uBlending.value = CONFIG[this.tier].blending;
        this.material.uniforms.uBlendMode.value = CONFIG[this.tier].blendMode;
        this.material.uniforms.uReverseBlend.value = CONFIG[this.tier].reverseBlend ? 1.0 : 0.0;
        this.material.uniforms.uNormalMapIntensity.value = CONFIG[this.tier].normalIntensity;
        this.material.uniforms.uNormalMapScale.value = CONFIG[this.tier].normalScale;

        this.rotation.x = Math.PI * GLOBAL_CONFIG.rotationX;
    }

    delete() {
        if (this.material.uniforms.uTexture.value) {
            this.material.uniforms.uTexture.value.dispose();
        }

        this.material.dispose();
    }
}
